import React, { useEffect, useRef, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import { ElementWrapper, HighlightPanel } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useRotationStore } from 'src/context/RotationContext';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { UUID } from 'crypto';
import {
    createBerthCallChangeData,
    createBerthCallOrderChange,
} from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/BerthCallChangeDataFactory';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { RotationItemPlaceHolder } from 'src/types/ViewModel/Stowage/Rotation/RotationItemPlaceHolder';
import { getBerthCalls } from 'src/utils/stowage/stowageRotationUtils';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';

type BerthVM = {
    berthId: number;
    berthName: string;
    berthSequenceNumber: number;
    voyageNumber: string;
    portCallGuid: UUID;
    portCall: RotationPortCall;
    notValidFieldsArray?: [];
    hasInvalidFields?: boolean;
};

type Props = {
    rotationItemPlaceHolder: RotationItemPlaceHolder;
    onClosePanel: () => void;
};

const BerthCallRotationPanel: React.FC<Props> = ({ rotationItemPlaceHolder, onClosePanel }) => {
    const [berthVM, setBerthVM] = useState<BerthVM>(null);
    const { activeStowagePlan } = useStowageStore();
    const { berthNameList } = useRotationStore();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveStowageData } = usePostStowageApi();
    const { setQueryParamStowagePortCall } = useStowageQueryParamsUtils();
    const [berthSequenceStowagePlanChanges, setBerthSequenceStowagePlanChanges] = useState<StowagePlanChange[]>([]);
    const isAddBerth = rotationItemPlaceHolder.type === 'add';
    const changedData = useRef<any>({});

    useEffect(() => {
        if (!activeStowagePlan || !rotationItemPlaceHolder) return;

        if (isAddBerth) {
            const _berthSequenceNumber =
                rotationItemPlaceHolder.parentLevel === 'berth'
                    ? rotationItemPlaceHolder.selectedBerthCall.berthSequenceNumber + 1
                    : Math.max(...getBerthCalls(rotationItemPlaceHolder.selectedPortCall).map((b) => b.berthSequenceNumber)) + 1;

            const _portCall =
                rotationItemPlaceHolder.parentLevel === 'berth'
                    ? rotationItemPlaceHolder.selectedBerthCall.portCall
                    : rotationItemPlaceHolder.selectedPortCall;

            setBerthVM({
                berthId: 0,
                berthName: '',
                berthSequenceNumber: _berthSequenceNumber,
                voyageNumber: _portCall.voyageNumber,
                portCall: _portCall,
                portCallGuid: _portCall.portCallGuid,
            });

            if (rotationItemPlaceHolder.parentLevel === 'berth') {
                const _berthCalls = rotationItemPlaceHolder.selectedBerthCall.portCall.berthCalls;
                if (_berthCalls) {
                    const _berthSequenceChanges = _berthCalls
                        .slice(
                            _berthCalls.findIndex((bc) => bc.berthSequenceNumber === rotationItemPlaceHolder.selectedBerthCall.berthSequenceNumber) +
                                1
                        )
                        .map((berthCall, index) =>
                            createBerthCallOrderChange(
                                activeStowagePlan.stowagePlanGuid,
                                berthCall.berthCallGuid,
                                _berthSequenceNumber + index + 1,
                                berthCall
                            )
                        );
                    setBerthSequenceStowagePlanChanges(_berthSequenceChanges);
                }
            }
        } else {
            const { berthId, berthName, berthSequenceNumber } = rotationItemPlaceHolder.selectedBerthCall;
            const { voyageNumber, portCallGuid } = rotationItemPlaceHolder.selectedPortCall;

            setBerthVM({
                berthId,
                berthName,
                berthSequenceNumber,
                voyageNumber,
                portCall: rotationItemPlaceHolder.selectedPortCall,
                portCallGuid,
            });
        }
    }, []);

    const onCancel = () => {
        onClosePanel();
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const {
            target: { name, value, type },
        } = changeObject.event;
        const normalizedValue = type === 'select-one' ? (value === 'Select berth' ? 0 : +value || '') : value ? +value : 0;

        changedData.current[name] = normalizedValue;
        berthVM[name] = normalizedValue;

        hasInvalidFields(berthVM, name, changeObject.isValid);
        setBerthVM({ ...berthVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const createAddBertCallChangeData = () => {
        return {
            berthId: +berthVM.berthId,
            berthSequenceNumber: berthVM.berthSequenceNumber,
            dataSource: 'OrcaWeb',
            portCallGuid: berthVM.portCall.portCallGuid,
        };
    };

    const onSave = async () => {
        const berthCallGuid = isAddBerth ? crypto.randomUUID() : rotationItemPlaceHolder.selectedBerthCall.berthCallGuid;
        const changeData = isAddBerth ? createAddBertCallChangeData() : changedData.current;

        let stowagePlanChanges = [
            createStowagePlanChange(
                activeStowagePlan.stowagePlanGuid,
                berthCallGuid,
                StowageChangeDataType.RotationBerthCall,
                createBerthCallChangeData(changeData)
            ),
        ];

        if (isAddBerth && rotationItemPlaceHolder.parentLevel && rotationItemPlaceHolder.parentLevel === 'berth') {
            stowagePlanChanges = stowagePlanChanges.concat(berthSequenceStowagePlanChanges);
        }

        await saveStowageData(activeStowagePlan, stowagePlanChanges);

        changedData.current = {};
        onClosePanel();
        setQueryParamStowagePortCall(berthVM.portCallGuid);
    };

    const canSave = berthVM?.berthId > 0 && !berthVM.hasInvalidFields;

    const getBerthOptionsList = () => {
        return (
            berthNameList
                ?.filter((b) => b.portId === berthVM.portCall.portId && !b.isDeleted)
                .sort((a, b) => a.longName.toLowerCase().localeCompare(b.longName.toLowerCase()))
                .map((berthItem) => ({
                    value: berthItem.berthId,
                    label: berthItem.longName,
                })) || []
        );
    };

    return (
        berthVM && (
            <OdfjellPanel
                headerTitle={isAddBerth ? 'Add berth call' : 'Edit berth call'}
                panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
                defaultPosition="center"
                onCancel={onCancel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={!canSave}
            >
                <HighlightPanel>
                    <div className="left-wrapper">
                        <ElementWrapper>
                            <OdfjellInputComponent
                                isReadOnly={false}
                                type={'select'}
                                description={'Berth'}
                                value={berthVM.berthId}
                                name={'berthId'}
                                dataObject={berthVM}
                                leaveBlank={true}
                                onChange={onInputChange}
                                options={getBerthOptionsList()}
                                tabIndex={1}
                                disableUnit={true}
                                selectDefaultOption={'Select berth'}
                                key={'input_cargo_temp'}
                            />
                        </ElementWrapper>
                    </div>
                </HighlightPanel>
            </OdfjellPanel>
        )
    );
};

export default BerthCallRotationPanel;
